/**
 * Admin — Vibe apps that cannot deploy
 *
 * The fleet-wide answer to "how many more of these are already out there?".
 *
 * An app whose git project never resolves absorbs pushes and builds nothing.
 * The tenant-facing console now names that state per app (`deployability`),
 * and the receive hook now tells whoever pushed — but both are per-app and
 * reactive: they surface the problem to someone who is already looking at the
 * app, or who just pushed to it. Nobody complained about the ones nobody has
 * touched in a month, so nothing finds them. This surface is the sweep.
 *
 * Cross-org by design. `AdminPermissionGuard` enforces PBAC (`admin:vibe-app`)
 * and `AdminAuditInterceptor` records the operator; there is no JWT org scope
 * here, which is exactly why the attach body names its `organizationId`
 * explicitly and the use case behind it re-resolves both projects inside that
 * org. See `AttachVibeGitProjectToAppUseCase` — the org scoping there is the
 * only thing between an operator's one click and a cross-tenant source link.
 */

import { z } from "zod";

import {
  VibeAppDataRunKindSchema,
  VibeAppDataRunOutcomeReadSchema
} from "../../../shared/domain/vibe/app-data-run-kind";
import { VibeAppDeployabilityReadSchema } from "../../../shared/domain/vibe/app-deployability";
import { ApiSuccessSchema } from "../../common/response.schema";
import { VibeGitProjectStatusReadSchema } from "../vibe/schemas/status-read.schemas";

// ============================================================
// List — every app with no source, fleet-wide
// ============================================================

/**
 * The same-named live git project in the app's own org, when exactly one
 * exists. This is the one-click candidate.
 *
 * Matching on name is sound because the database says so: `VibeGitProject`
 * carries a partial unique on `(organizationId, name) WHERE deletedAt IS NULL`,
 * so "the live project called X in this org" is either one row or none. It is
 * also how provisioning names a project in the first place — a project minted
 * for an app takes the app's name — which is what makes the coincidence
 * meaningful rather than a guess.
 *
 * `status` is carried rather than filtered on. Attaching is deliberately NOT
 * gated on project status (a PENDING project is still materializing and
 * attaching early is legitimate; the deploy path refuses a project with no
 * remote); re-deciding that here would create a second opinion that can drift
 * from the use case's. The operator sees the status and decides.
 */
export const AdminVibeOrphanAppCandidateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: VibeGitProjectStatusReadSchema
});

/**
 * One app that cannot deploy because nothing is attached to it.
 *
 * `danglingGitProjectId` separates the two ways an app arrives here, which
 * look identical to every other reader and are operationally different:
 *
 *   - `null` — no project was ever attached. The original `horaire` shape:
 *     someone created the app, someone else created the repo, nothing joined
 *     them.
 *   - set — the app points at a project id that resolves to nothing: the
 *     project was soft-deleted, or the id belongs to another org (the FK
 *     carries no organization, so the database permits it). The app reads as
 *     unattached everywhere, and this is the only place the stale id is
 *     visible at all.
 */
export const AdminVibeOrphanAppSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  organizationId: z.string(),
  /// The org's display name, so the table reads as names rather than opaque
  /// ids. Null when the org row is gone — the UI falls back to the id and
  /// never fabricates a name.
  organizationName: z.string().nullable().catch(null),
  /// Always `NO_SOURCE_ATTACHED` today — carried rather than implied so the
  /// row states its own finding, and so widening this report to other
  /// non-deployable states is a filter change, not a schema change.
  deployability: VibeAppDeployabilityReadSchema,
  danglingGitProjectId: z.string().uuid().nullable(),
  candidateGitProject: AdminVibeOrphanAppCandidateSchema.nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export const AdminVibeOrphanAppListQuerySchema = z.object({
  /// Page size. Capped server-side by the schema itself — an unbounded admin
  /// list is how a fleet page becomes a database incident once the fleet is
  /// large enough to matter.
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional()
});

export const AdminVibeOrphanAppListResponseSchema = z.object({
  items: z.array(AdminVibeOrphanAppSchema),
  /// Total orphans fleet-wide, independent of the page. The number IS the
  /// report: "3" and "300" call for entirely different responses, and a page
  /// of 50 cannot tell them apart.
  total: z.number().int().nonnegative()
});

// ============================================================
// Attach — the fix, from the row that found the problem
// ============================================================

/**
 * `organizationId` is required and is not decoration. This surface has no JWT
 * org scope, so it is the only scope the use case gets; it resolves BOTH the
 * requested project and the app within that org, and a mismatch surfaces as a
 * 404 rather than a cross-tenant attach. The UI sends the org id off the row
 * it is acting on, never one the operator typed.
 */
export const AdminVibeAppAttachSourceBodySchema = z
  .object({
    organizationId: z.string().min(1),
    gitProjectId: z.string().uuid()
  })
  .strict();

export const AdminVibeAppAttachSourceResponseSchema = z.object({
  vibeAppId: z.string().uuid(),
  gitProjectId: z.string().uuid(),
  gitProjectName: z.string(),
  gitProjectStatus: VibeGitProjectStatusReadSchema
});

export const AdminVibeAppPathVarsSchema = z.object({
  id: z.string().uuid()
});

// ============================================================
// Endpoint definitions
// ============================================================

// ============================================================
// Request data run — the app-family backup / drill operator lever
// ============================================================

/**
 * Ask for ONE app's dump or restore drill to be re-run today (NEX-3196).
 *
 * 🔴 THIS DISPATCHES NOTHING, AND THE RESPONSE IS CAREFUL TO SAY SO. The app
 * family runs as a `pg_dump` / `pg_restore` pipeline on the tenant's OWN Nomad,
 * behind the tenant boundary the control plane has no route across — so unlike
 * the tenant-family lever next door (`ZAdminVibeTenantCluster.RunBackupNow`,
 * which really does call AWS Backup and can report a job id), this records a
 * request the tenant's agent converges to on its next poll. `outcome` is
 * therefore `requested`, never `succeeded`.
 *
 * `reason` mirrors the tenant lever's: recorded in the admin audit log, and
 * defaulted by the caller to the issue code it was clicked from.
 */
export const AdminVibeAppRequestDataRunBodySchema = z.object({
  organizationId: z.string().min(1),
  kind: VibeAppDataRunKindSchema,
  reason: z.string().trim().min(1).max(500)
});

export const AdminVibeAppRequestDataRunResponseSchema = z.object({
  outcome: VibeAppDataRunOutcomeReadSchema,
  /**
   * The UTC window the request applies to, or null when it was refused.
   *
   * Surfaced rather than kept internal because it is the honest bound on what
   * was just asked for: the request lapses at the end of this window, so an
   * operator who clicks at 23:59 UTC and sees nothing happen has the one fact
   * that explains it.
   */
  window: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
});

export const ZAdminVibeApp = {
  ListOrphans: {
    method: "GET" as const,
    path: "/api/admin/vibe/apps/orphans",
    Query: AdminVibeOrphanAppListQuerySchema,
    Response: ApiSuccessSchema(AdminVibeOrphanAppListResponseSchema)
  },
  AttachSource: {
    method: "POST" as const,
    path: "/api/admin/vibe/apps/:id/attach-source",
    PathVars: AdminVibeAppPathVarsSchema,
    Body: AdminVibeAppAttachSourceBodySchema,
    Response: ApiSuccessSchema(AdminVibeAppAttachSourceResponseSchema)
  },
  RequestDataRun: {
    method: "POST" as const,
    path: "/api/admin/vibe/apps/:id/request-data-run",
    PathVars: AdminVibeAppPathVarsSchema,
    Body: AdminVibeAppRequestDataRunBodySchema,
    Response: ApiSuccessSchema(AdminVibeAppRequestDataRunResponseSchema)
  }
} as const;

// ============================================================
// Convenience type exports
// ============================================================

export type AdminVibeOrphanAppCandidate = z.infer<typeof AdminVibeOrphanAppCandidateSchema>;
export type AdminVibeOrphanApp = z.infer<typeof AdminVibeOrphanAppSchema>;
export type AdminVibeOrphanAppListResponse = z.infer<typeof AdminVibeOrphanAppListResponseSchema>;
export type AdminVibeAppAttachSourceBody = z.infer<typeof AdminVibeAppAttachSourceBodySchema>;
export type AdminVibeAppRequestDataRunBody = z.infer<typeof AdminVibeAppRequestDataRunBodySchema>;
export type AdminVibeAppRequestDataRunResponse = z.infer<
  typeof AdminVibeAppRequestDataRunResponseSchema
>;
